import { type ComputedRef, onActivated, onDeactivated, onMounted, onUnmounted, type WatchStopHandle, watch } from 'vue';

import { useStore } from '@/store';

// Keep ownership metadata outside Pinia state: it is lifecycle-local and should not be serialized
// or exposed as application state. A newer component claim always replaces the previous token.
const subAppNameClaims = new WeakMap<object, symbol>();

// Root app name (sidebar wordmark + breadcrumb) is now driven by route meta via
// a central router.afterEach guard — see router.ts. There's no useRootAppName
// composable anymore: it relied on component lifecycle, which flickered the
// default during route transitions and lingered on routes that didn't set it.
//
// A dynamic sub-app name lives in its own store field (`dynamicSubAppName`) rather than sharing the
// route-meta one: the guard runs on every navigation, so a shared field meant a child route that
// declares no name of its own — a site tab, say — wiped the name its still-mounted parent owns.

export function useSubAppName(name: string | ComputedRef<string>) {
    const store = useStore();
    const claim = Symbol('sub-app-name');

    let nameStr = typeof name === 'string' ? name : name.value;
    let stopNameWatch: WatchStopHandle | undefined;

    bindHooks(
        () => {
            // A kept-alive component can be reactivated after its computed name changed while it
            // was away. Pick up that latest value before claiming the breadcrumb again.
            nameStr = typeof name === 'string' ? name : name.value;
            subAppNameClaims.set(store, claim);
            store.dynamicSubAppName = nameStr;
            if (typeof name !== 'string') {
                stopNameWatch = watch(name, newVal => {
                    if (subAppNameClaims.get(store) !== claim) return;
                    store.dynamicSubAppName = newVal;
                    nameStr = newVal;
                });
            }
        },
        () => {
            stopNameWatch?.();
            stopNameWatch = undefined;

            // Only clear our own claim — another screen may own the same displayed name.
            if (subAppNameClaims.get(store) === claim) {
                subAppNameClaims.delete(store);
                store.dynamicSubAppName = null;
            }
        }
    );
}

export function useIsMobileReady() {
    const store = useStore();
    bindHooks(
        () => {
            store.isMobileReady = true;
        },
        () => {
            store.isMobileReady = false;
        }
    );
}

function bindHooks(inHook: () => void, outHook: () => void) {
    let isActive = false;

    function activate() {
        // Kept-alive components receive both mounted and activated on first render. Only enter
        // once, otherwise composables that create watchers would leave duplicate subscriptions.
        if (isActive) return;
        isActive = true;
        inHook();
    }

    function deactivate() {
        if (!isActive) return;
        isActive = false;
        outHook();
    }

    onMounted(activate);
    onActivated(activate);
    onDeactivated(deactivate);
    onUnmounted(deactivate);
}
